/* Predictive search (Shopify Predictive Search API, section rendering).
   Debounce 300 ms, aborts stale requests, listbox keyboard navigation. */
(() => {
  const ik = (window.ikTheme = window.ikTheme || {});

  class IkPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.form = this.querySelector('form');
      this.clearButton = this.querySelector('[data-predictive-clear]');
      this.sectionId = this.dataset.section || 'predictive-search';
      this.inline = this.hasAttribute('data-inline');
      this.cache = new Map();
      this.controller = null;
      this.lastQuery = '';
    }

    connectedCallback() {
      if (!this.input || !this.results) return;
      const debounce = ik.debounce || ((fn) => fn);
      this.input.addEventListener('input', debounce(() => this.onInput(), 300));
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      if (this.clearButton) this.clearButton.addEventListener('click', () => this.clear());
      this.results.addEventListener('mousedown', (event) => event.preventDefault());

      if (!this.inline) {
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.close();
        });
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.close();
        });
      }
      this.updateClear();
    }

    get query() {
      return this.input.value.trim();
    }

    updateClear() {
      if (this.clearButton) this.clearButton.hidden = this.query.length === 0;
    }

    onInput() {
      this.updateClear();
      const query = this.query;
      if (!query) {
        this.close(true);
        return;
      }
      if (query === this.lastQuery && this.results.innerHTML.trim()) {
        this.open();
        return;
      }
      this.fetchResults(query);
    }

    onFocus() {
      if (this.query && this.results.innerHTML.trim()) this.open();
    }

    onSubmit(event) {
      if (!this.query) event.preventDefault();
    }

    fetchResults(query) {
      this.lastQuery = query;
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('loading', '');

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        section_id: this.sectionId
      });
      const url = `${ik.routes.predictiveSearch}?${params.toString()}`;

      fetch(url, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector(`#shopify-section-${this.sectionId}`) || doc.body;
          const markup = section.innerHTML;
          this.cache.set(query, markup);
          if (query === this.query) this.render(markup);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') this.close(true);
        })
        .finally(() => {
          this.removeAttribute('loading');
        });
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.activeIndex = -1;
      this.open();
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    open() {
      if (!this.results.innerHTML.trim()) return;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (!this.inline) document.body.classList.add('predictive-open');
    }

    close(clearResults = false) {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
      if (!this.inline) document.body.classList.remove('predictive-open');
      if (clearResults) {
        this.results.innerHTML = '';
        this.lastQuery = '';
      }
      this.activeIndex = -1;
    }

    clear() {
      this.input.value = '';
      this.updateClear();
      this.close(true);
      this.input.focus();
    }

    setActive(index) {
      const options = this.options;
      options.forEach((option, i) => {
        const active = i === index;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          if (!option.id) option.id = `predictive-option-${this.sectionId}-${i}`;
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
      this.activeIndex = index;
    }

    onKeydown(event) {
      const options = this.options;
      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          if (this.results.hidden) this.open();
          this.setActive(Math.min(options.length - 1, (this.activeIndex ?? -1) + 1));
          break;
        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.setActive(Math.max(0, (this.activeIndex ?? 0) - 1));
          break;
        case 'Enter':
          if (this.activeIndex >= 0 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click();
          }
          break;
        case 'Escape':
          if (!this.results.hidden) {
            event.preventDefault();
            event.stopPropagation();
            this.close();
          }
          break;
        default:
      }
    }
  }

  if (!customElements.get('ik-predictive-search')) {
    customElements.define('ik-predictive-search', IkPredictiveSearch);
  }
})();
